/**
 * Registry globale per voci di ricerca dinamiche dello Spotlight.
 *
 * Le pagine si registrano via `useRegisterSearch(scope, entries)` per esporre
 * i propri titoli/bottoni alla palette globale di ricerca. Lo SpotlightProvider
 * subscribe a queste voci e le mostra nel gruppo "Su questa pagina".
 */
export interface SearchEntry {
  /** ID stabile (univoco nello scope). */
  id: string;
  /** Etichetta principale visualizzata. */
  label: string;
  /** Descrizione opzionale (un mini hint sotto la label). */
  description?: string;
  /** Keywords aggiuntive per il fuzzy match. */
  keywords?: string[];
  /** Sezione mostrata nello Spotlight (default: "Azioni di pagina"). */
  group?: string;
  /** Rotta TanStack di destinazione, o callback custom. */
  to?: string;
  onSelect?: () => void;
  /** Icon component opzionale (tipato come componente JSX in chiamante). */
  iconName?: string;
}

type Listener = () => void;

const scopes = new Map<string, SearchEntry[]>();
const listeners = new Set<Listener>();
let snapshot: SearchEntry[] = [];

function rebuildSnapshot() {
  const out: SearchEntry[] = [];
  for (const v of scopes.values()) out.push(...v);
  snapshot = out;
}

function notify() {
  rebuildSnapshot();
  for (const l of listeners) l();
}

export function registerSearchEntries(scope: string, entries: SearchEntry[]): () => void {
  scopes.set(scope, entries);
  notify();
  return () => {
    if (scopes.get(scope) === entries) {
      scopes.delete(scope);
      notify();
    }
  };
}

/** Ritorna lo snapshot corrente — stable per riferimento finché non cambia il registry.
 *  Necessario per useSyncExternalStore: ogni call deve dare lo stesso ref se lo stato non è cambiato. */
export function getAllSearchEntries(): SearchEntry[] {
  return snapshot;
}

export function subscribeSearchRegistry(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
